import React, { useEffect, useState } from 'react';
import { Heart, CheckCircle2 } from 'lucide-react';

/**
 * Shows a celebratory burst when the user earns points or completes a task.
 * Auto-dismisses after ~1.8 seconds.
 *
 * Kinds:
 *   'love'      — partner-sent task completed on time (red heart, point counter, "Kærlighedspoint")
 *   'late'      — partner-sent task completed late
 *   'early'     — partner-sent task completed early (gold)
 *   'celebrate' — own private/shared task (no points, just confetti)
 */
export function PointBurst({ theme, points, kind = 'love', onDone }) {
  const [show, setShow] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setShow(false);
      setTimeout(onDone, 300);
    }, 1800);
    return () => clearTimeout(t);
  }, [onDone]);

  if (!show) return null;

  const isCelebrate = kind === 'celebrate';

  const accentColor =
    kind === 'late' ? '#EF4444' :
    kind === 'early' ? theme.gold :
    isCelebrate ? theme.teal :
    theme.coral;

  const particleCount = isCelebrate ? 24 : 18;
  const particles = Array.from({ length: particleCount }, (_, i) => ({
    id: i,
    delay: Math.random() * 0.3,
    x: (Math.random() - 0.5) * 280,
    y: -Math.random() * 180 - 60,
    rotate: Math.random() * 720 - 360,
    color: [theme.coral, theme.gold, theme.teal, '#EC4899'][i % 4],
    size: Math.random() * 6 + 4,
  }));

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 400,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <style>{`
        @keyframes tt-burst-in {
          0% { transform: scale(0.4); opacity: 0; }
          50% { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes tt-confetti {
          0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          100% { transform: translate(var(--tx), var(--ty)) rotate(var(--tr)); opacity: 0; }
        }
        .tt-burst {
          animation: tt-burst-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      {/* Confetti */}
      {particles.map(p => (
        <div key={p.id} style={{
          position: 'absolute',
          width: p.size,
          height: p.size,
          background: p.color,
          borderRadius: 2,
          animation: `tt-confetti 1.5s ease-out forwards`,
          animationDelay: `${p.delay}s`,
          ['--tx']: `${p.x}px`,
          ['--ty']: `${p.y}px`,
          ['--tr']: `${p.rotate}deg`,
        }} />
      ))}

      {/* Main burst card */}
      {isCelebrate ? (
        <div className="tt-burst" style={{
          background: theme.bgElevated,
          border: `2px solid ${accentColor}`,
          borderRadius: 20,
          padding: '20px 28px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          boxShadow: `0 20px 60px ${accentColor}40`,
        }}>
          <CheckCircle2 size={36} color={accentColor} fill={`${accentColor}20`} />
          <div style={{ fontSize: 14, fontWeight: 700, color: theme.text }}>
            Sådan!
          </div>
        </div>
      ) : (
        <div className="tt-burst" style={{
          background: theme.bgElevated,
          border: `2px solid ${accentColor}`,
          borderRadius: 20,
          padding: '28px 32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          boxShadow: `0 20px 60px ${accentColor}40`,
          minWidth: 240,
        }}>
          <Heart size={28} color={accentColor} fill={accentColor} />
          <div style={{ fontSize: 38, fontWeight: 800, color: accentColor, lineHeight: 1 }}>
            +{points}
          </div>
          <div style={{ fontSize: 11, color: theme.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
            {kind === 'late' ? 'Bedre sent end aldrig' : kind === 'early' ? 'Bonus for tidlig levering!' : 'Kærlighedspoint'}
          </div>
        </div>
      )}
    </div>
  );
}
